// apps/roleforge-app/src/pages/MaterializePage.tsx
// One-click RoleForge activation progress route.

import { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { navigateToExternalUrl } from '../lib/external-navigation';
import {
  fetchMaterializeProposal,
  fetchMaterializeStatus,
  fetchTeam,
  type AuditTeamResponse,
  type MaterializeStatusResponse,
} from '../lib/materialize-api';
import { MaterializeProgressDialog } from '../components/materialize/MaterializeProgressDialog';

export function MaterializePage() {
  const { id: auditId } = useParams<{ id: string }>();
  const { jwt } = useAuth();
  const [team, setTeam] = useState<AuditTeamResponse | null>(null);
  const [materializeStatus, setMaterializeStatus] =
    useState<MaterializeStatusResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  /**
   * Issue #1323 (1) — "Try again" used to be `window.location.reload()`. On
   * reload `audit_status` is still `failed`, so neither `shouldActivate` nor
   * `shouldResume` was true and `MaterializeProgressDialog` — the ONLY
   * component that calls `postMaterialize` — never mounted. The identical
   * error screen re-rendered and the server's reclaim path was unreachable
   * from the UI. Clicking retry now actually re-POSTs.
   */
  const [retryRequested, setRetryRequested] = useState(false);
  /**
   * Task 5 (#1738) — MaterializeProgressDialog no longer fetches the proposal
   * itself; on this one-click activation path the page reads the version and
   * hands it down. `undefined` = read still in flight; `null` = read failed,
   * confirm unversioned. The read is deliberately NOT allowed to block the
   * money path: any failure resolves to `null`, exactly the behaviour this
   * flow had before the review screen existed. A missing version can only
   * lose a staleness check, never apply a wrong Blueprint.
   */
  const [blueprintVersion, setBlueprintVersion] = useState<string | null | undefined>(undefined);

  useEffect(() => {
    if (!auditId || !jwt) return;
    let cancelled = false;
    fetchMaterializeProposal(auditId, jwt)
      .then((proposal) => {
        if (!cancelled) setBlueprintVersion(proposal.blueprintVersion ?? null);
      })
      .catch((err: unknown) => {
        console.warn('[roleforge] blueprint proposal read failed; confirming unversioned', err);
        if (!cancelled) setBlueprintVersion(null);
      });
    return () => {
      cancelled = true;
    };
  }, [auditId, jwt]);

  // The dialog POSTs on mount and then polls itself, so whenever it is on
  // screen it is the single poller for this page (#1323 (4)).
  const dialogOwnsPolling =
    !!jwt
    // The dialog mounts only after the proposal read settles (it needs the
    // version as a prop), so until then the page keeps polling ownership.
    && blueprintVersion !== undefined
    && (retryRequested
      || team?.audit_status === 'materializing'
      || (team?.can_materialize === true && team?.audit_status === 'ready_to_materialize'));

  useEffect(() => {
    if (!auditId || !jwt) return;
    fetchTeam(auditId, jwt)
      .then(setTeam)
      .catch((e: Error) => setError(e.message));
  }, [auditId, jwt]);

  useEffect(() => {
    if (!auditId || !jwt || !team) return;
    if (!['materializing', 'materialized', 'failed'].includes(String(team.audit_status))) {
      return;
    }
    // Issue #1323 (4) — `buildWelcomeRedirect` mints a fresh SINGLE-USE magic
    // link on EVERY materialize-status GET, and each `generateLink` invalidates
    // the previous one. On the resume path this page ran its own poll loop AND
    // mounted MaterializeProgressDialog, which runs another — so the two
    // pollers raced and the buyer was navigated to a link the sibling had
    // already invalidated, landing on "This link has expired or was already
    // used" after a successful purchase. There is now exactly one poller: when
    // the dialog is mounted, it owns polling.
    if (dialogOwnsPolling) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const poll = async () => {
      try {
        const status = await fetchMaterializeStatus(auditId, jwt);
        if (cancelled) return;
        setMaterializeStatus(status);
        if (status.status === 'materializing') {
          timer = setTimeout(() => void poll(), Math.max(status.retry_after_ms, 250));
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      }
    };

    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [auditId, jwt, team, dialogOwnsPolling]);

  const redirectUrl =
    materializeStatus?.status === 'materialized' && materializeStatus.redirect_url
      ? materializeStatus.redirect_url
      : null;

  useEffect(() => {
    if (redirectUrl) navigateToExternalUrl(redirectUrl);
  }, [redirectUrl]);

  if (!auditId) return <Navigate to="/" />;
  if (error) return <div className="p-8 text-ember">Error: {error}</div>;
  if (!team) return <div className="p-8 text-graphite">Loading…</div>;

  if (redirectUrl) {
    return (
      <div className="min-h-screen bg-paper text-ink" data-testid="materialize-page">
        <main className="mx-auto flex min-h-screen max-w-3xl flex-col justify-center px-6 py-12">
          <p className="text-sm uppercase tracking-[0.2em] text-graphite">Step 4 of 4</p>
          <h1 className="mt-3 text-3xl font-bold">Sending you to VibeControl</h1>
          <p className="mt-4 max-w-2xl text-graphite">
            Your workspace is ready. If you are not redirected automatically, open VibeControl below.
          </p>
          <a
            href={redirectUrl}
            className="mt-8 w-fit rounded bg-plum px-4 py-2 text-sm font-semibold text-paper hover:bg-plum/90"
          >
            Open VibeControl
          </a>
        </main>
      </div>
    );
  }
  if (team.audit_status === 'materialized' && materializeStatus?.status !== 'materializing') {
    return <Navigate to="/audits" />;
  }
  const failed = materializeStatus?.status === 'failed' || team.audit_status === 'failed';
  if (failed && !retryRequested) {
    const message =
      materializeStatus?.status === 'failed'
        ? materializeStatus.message
        : 'We could not finish setup automatically. Your Blueprint is saved. Please try again or contact support.';
    // The server tells us whether a retry can actually do anything. Only an
    // unknown answer is treated as retryable — an explicit `false` must not
    // offer a button that will refuse.
    const retriable =
      materializeStatus?.status === 'failed' ? materializeStatus.retriable !== false : true;
    return (
      <div className="min-h-screen bg-paper text-ink" data-testid="materialize-page">
        <main className="mx-auto flex min-h-screen max-w-3xl flex-col justify-center px-6 py-12">
          <p className="text-sm uppercase tracking-[0.2em] text-graphite">Step 4 of 4</p>
          <h1 className="mt-3 text-3xl font-bold">Setup needs attention</h1>
          <p className="mt-4 max-w-2xl text-graphite">{message}</p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            {retriable && (
              <button
                type="button"
                onClick={() => setRetryRequested(true)}
                data-testid="materialize-page-retry"
                className="w-fit rounded bg-plum px-4 py-2 text-sm font-semibold text-paper hover:bg-plum/90"
              >
                Try again
              </button>
            )}
            {/* #1323 (1) — a retry that the server will refuse leaves the buyer
                with nothing to do. There is always a way to reach a human. */}
            <a
              href="mailto:support@vibecontrol.app"
              data-testid="materialize-page-support-link"
              className="text-sm text-graphite underline underline-offset-2 hover:text-ink"
            >
              Contact support
            </a>
          </div>
        </main>
      </div>
    );
  }

  const shouldActivate =
    team.can_materialize === true && team.audit_status === 'ready_to_materialize';
  const shouldResume = team.audit_status === 'materializing' || retryRequested;

  if (!shouldActivate && !shouldResume) {
    // #1329: this used to be a bare `<div className="p-8 text-graphite">` that
    // interpolated the raw `audit_status` ENUM at a customer, while both sibling
    // states above and below got the full `min-h-screen` shell. Same shell, and
    // the enum stays in a `data-` attribute for support instead of in the copy.
    return (
      <div
        className="min-h-screen bg-paper text-ink"
        data-testid="materialize-not-ready"
        data-audit-status={team.audit_status ?? 'unknown'}
      >
        <main className="mx-auto flex min-h-screen max-w-3xl flex-col justify-center px-6 py-12">
          <p className="text-sm uppercase tracking-[0.2em] text-graphite">Step 4 of 4</p>
          <h1 className="mt-3 text-3xl font-bold">Not quite ready yet</h1>
          <p className="mt-4 max-w-2xl text-graphite">
            Your Blueprint still has a step to finish before we can set up your workspace. Nothing has
            been lost — pick up where you left off and this screen will let you through.
          </p>
          {/* Review fix: the copy promised "pick up where you left off" and then
              rendered nothing to do it with. `audits/:id` is the Blueprint shell
              this page was reached from. */}
          {auditId ? (
            <Link
              to={`/audits/${auditId}`}
              data-testid="materialize-not-ready-resume"
              className="mt-8 w-fit rounded bg-plum px-4 py-2 text-sm font-semibold text-paper hover:bg-plum/90"
            >
              Back to your Blueprint
            </Link>
          ) : null}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper text-ink" data-testid="materialize-page">
      <main className="mx-auto grid min-h-screen w-full max-w-5xl grid-cols-1 items-center gap-8 px-6 py-12 lg:grid-cols-[minmax(0,1fr)_minmax(360px,420px)] lg:px-8">
        <section>
          <p className="text-sm uppercase tracking-[0.2em] text-graphite">Step 4 of 4</p>
          <h1 className="mt-3 text-3xl font-bold sm:text-4xl">Setting up your workforce in VibeControl</h1>
          <p className="mt-4 max-w-2xl text-graphite">
            Setting up your departments, seats, and skills — plus your operating model, CRM basics, and site setup — from your Blueprint.
          </p>
          <div className="mt-8 rounded-lg border border-line bg-white/5 p-5 text-sm text-graphite">
            You will be sent to VibeControl when your workspace is ready.
          </div>
        </section>

        {jwt && blueprintVersion !== undefined && (
          <MaterializeProgressDialog
            auditId={auditId}
            jwt={jwt}
            blueprintVersion={blueprintVersion}
            dismissible={false}
            variant="inline"
            onRedirect={navigateToExternalUrl}
          />
        )}
      </main>
    </div>
  );
}
